import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchGetProfile,
  fetchUserPushes,
  markPushAsRead,
  subscribeDevice,
  fetchUpdateProfile,
  fetchUploadAvatar,
  mggFetchUserRecommendedVideos,
  userChangePassword
} from '../../api/userApi';
import { FETCH_STATUS_FAILED, FETCH_STATUS_LOADING, FETCH_STATUS_SUCCESS } from '../constants';

const initialState = {
  profile: {
    data: {
      avatar: '',
      name: '',
      email: null,
      phone: null,
      birthday: null,
      location: {},
      use_push_notifications: false
    },
    status: 'idle',
    error: null
  },
  userRecommendedVideoList: { list: [], status: 'idle' },
  userPushesList: {
    list: [],
    next_page_url: null,
    status: 'idle'
  },
  isOpenPushNotifications: false
};

export const getProfile = createAsyncThunk('user/getProfile', async (body) => {
  return await fetchGetProfile(body);
});

export const getPushes = createAsyncThunk('user/getPushes', async (page) => {
  return await fetchUserPushes(page);
});

export const markNotificationAsRead = createAsyncThunk('user/markNotificationAsRead', async (body) => {
  return await markPushAsRead(body);
});

export const subscribeForNotifications = createAsyncThunk('user/subscribeForNotifications', async (body) => {
  return await subscribeDevice(body);
});

export const updateProfile = createAsyncThunk('user/updateProfile', async (body) => {
  return await fetchUpdateProfile(body);
});

export const changePassword = createAsyncThunk('user/changePassword', async (body) => {
  return await userChangePassword(body);
});

export const uploadProfileImage = createAsyncThunk('user/uploadProfileImage', async (body) => {
  return await fetchUploadAvatar(body);
});

export const fetchUserRecommendedVideos = createAsyncThunk(
  'user/fetchUserRecommendedVideos',
  async ({ limit }) => {
    return await mggFetchUserRecommendedVideos(limit);
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetRecommendedVideos: (state) => {
      state.userRecommendedVideoList = initialState.userRecommendedVideoList;
    },
    openPushNotification: (state, action) => {
      state.isOpenPushNotifications = action.payload;
    },
    setUserDataProfile: (state, action) => {
      state.profile.data = action.payload;
    },
    removeProfileData: (state) => {
      state.profile = initialState.profile;
    }
  },
  extraReducers: {
    [getProfile.pending]: (state) => {
      state.profile.status = FETCH_STATUS_LOADING;
    },
    [getProfile.fulfilled]: (state, action) => {
      state.profile.status = FETCH_STATUS_SUCCESS;
      state.profile.data = action.payload.data;
    },
    [getProfile.rejected]: (state, action) => {
      state.profile.status = FETCH_STATUS_FAILED;
      state.profile.error = action.error;
    },
    [updateProfile.fulfilled]: (state, action) => {
      state.profile.data = action.payload.data;
    },
    [uploadProfileImage.pending]: (state) => {
      state.profile.status = FETCH_STATUS_LOADING;
    },
    [uploadProfileImage.fulfilled]: (state, action) => {
      state.profile.status = FETCH_STATUS_SUCCESS;
      state.profile.data.avatar = action.payload.data.avatar;
    },
    [uploadProfileImage.rejected]: (state) => {
      state.profile.status = FETCH_STATUS_FAILED;
    },
    [fetchUserRecommendedVideos.pending]: (state) => {
      state.userRecommendedVideoList.status = FETCH_STATUS_LOADING;
    },
    [fetchUserRecommendedVideos.rejected]: (state) => {
      state.userRecommendedVideoList.status = FETCH_STATUS_FAILED;
    },
    [fetchUserRecommendedVideos.fulfilled]: (state, action) => {
      state.userRecommendedVideoList.list = action.payload.data.video_list;
      state.userRecommendedVideoList.status = FETCH_STATUS_SUCCESS;
    },
    [getPushes.pending]: (state) => {
      state.userPushesList.status = FETCH_STATUS_LOADING;
    },
    [getPushes.rejected]: (state) => {
      state.userPushesList.status = FETCH_STATUS_FAILED;
    },
    [getPushes.fulfilled]: (state, action) => {
      if (action.payload.meta.current_page !== 1) {
        const next = action.payload.data.filter((i) => !state.userPushesList.list.find((p) => p.id === i.id));
        state.userPushesList.list = [...state.userPushesList.list, ...next].sort((a, b) => (a.id < b.id ? 1 : -1));
      } else {
        state.userPushesList.list = action.payload.data;
      }
      state.userPushesList.next_page_url = action.payload.links.next;
      state.userPushesList.status = FETCH_STATUS_SUCCESS;
    }
  }
});

// Actions
export const {
  resetRecommendedVideos,
  openPushNotification,
  setUserDataProfile,
  removeProfileData
} = userSlice.actions;

// Selectors
export const getUserProfile = (state) => state.user.profile.data;
export const getUserProfileStatus = (state) => state.user.profile.status;
export const getUserRecommendedVideoList = (state) => ({
  list: state.user.userRecommendedVideoList.list,
  status: state.user.userRecommendedVideoList.status
});
export const getIsOpenedPushNotifications = (state) => state.user.isOpenPushNotifications;
export const getUserPushesList = (state) => ({
  list: state.user.userPushesList.list,
  next_page_url: state.user.userPushesList.next_page_url,
  status: state.user.userPushesList.status
});

// Reducer
export default userSlice.reducer;
